import { useRollbar } from '@rollbar/react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { Stripe, StripeError } from '@stripe/stripe-js'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import {
  BadRequest,
  InternalError,
  NetworkError,
  ServiceUnavailable,
} from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import { optInFail } from 'publisher/actions/optInActions'
import {
  buyMainOfferWithStripe,
  confirmPaymentIntent,
} from 'publisher/api/stripe'
import { IntentTypeEnum } from 'publisher/enums/IntentTypeEnum'
import usePayment, {
  selectors as paymentSelectors,
} from 'publisher/hooks/usePayment'
import { useFinalAmount } from 'publisher/pages/offer-page/hooks/use-final-amount'
import { getActivePaymentMethod } from 'publisher/store/payment/paymentSelectors'
import React from 'react'
import { useDispatch } from 'react-redux'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import usePage, { selectors as pageSelectors } from '../usePage'
import usePaymentSubmit from './usePaymentSubmit'

const getStripeConfirmHandler = (stripe: Stripe, type: IntentTypeEnum) =>
  type === IntentTypeEnum.setup ? stripe.confirmSetup : stripe.confirmPayment

export const useStripeGeneralPaymentHandler = (
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) => {
  const dispatch = useDispatch()
  const { t } = useTypedTranslation()
  const rollbar = useRollbar()
  const stripe = useStripe()
  const elements = useElements()
  const purchaseProcessId = usePayment(paymentSelectors.getPurchaseProcessId)
  const activePaymentMethod = usePayment(getActivePaymentMethod)

  const pageId = usePage(pageSelectors.getPageId)
  const { errors, setErrors, isLoading, submit } = usePaymentSubmit(entity)
  const finalAmount = useFinalAmount()
  const isReady = !!stripe && !!elements
  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }
    await submit(
      async body => {
        const isFreeMultibanco =
          activePaymentMethod === PaymentMethodEnum.StripeMultibanco &&
          finalAmount === 0
        const isNameRequired =
          activePaymentMethod === PaymentMethodEnum.StripeIDeal ||
          activePaymentMethod === PaymentMethodEnum.StripeBancontact ||
          activePaymentMethod === PaymentMethodEnum.StripeKlarna ||
          activePaymentMethod === PaymentMethodEnum.StripeMultibanco
        const paymentMethodData = {
          billing_details: {
            email: body.customer.fields?.email,
            // address: {
            //   country: null,
            // },
            ...(isNameRequired
              ? {
                  name: [
                    body.customer.fields?.first_name,
                    body.customer.fields?.surname,
                  ]
                    .filter(Boolean)
                    .join(' '),
                }
              : {}),
          },
        }

        // todo it's a temp solution for https://systeme-team.slack.com/archives/C04BRAJJJ3G/p1758030924520599?thread_ts=1757928715.452419&cid=C04BRAJJJ3G
        body.paymentMethod =
          activePaymentMethod === 'stripe:apple_pay'
            ? 'stripe:applepay'
            : activePaymentMethod

        let paymentMethodId: string | undefined

        if (!isFreeMultibanco) {
          // does not actually submit anything, validates stripe elements
          // https://docs.stripe.com/js/elements/submit
          const { error: initError } = await elements.submit()

          if (initError) {
            // throw new Error('Stripe elements validation failed')
            return
          }

          const { error, confirmationToken } =
            await stripe.createConfirmationToken({
              elements,
              params: {
                payment_method_data: paymentMethodData,
              },
            })

          if (error) {
            // @see https://systeme-team.slack.com/archives/C6UURE03D/p1670840092361989?thread_ts=1668177169.203109&cid=C6UURE03D
            setErrors([error.message as string])
            return
          }

          if (!confirmationToken) {
            throw new Error('undefined payment method')
          }

          paymentMethodId = confirmationToken.id
        }

        const { data: mainOfferData } = await buyMainOfferWithStripe(
          pageId,
          purchaseProcessId,
          {
            payment_form: {
              ...body,
              ...(paymentMethodId ? { paymentMethodId } : {}),
              isNewStipePaymentElement: true,
            },
          },
        )

        if (mainOfferData.intentSecret) {
          let error: StripeError | undefined
          if (mainOfferData.isNextActionNeeded) {
            // Multibanco "next_action" is voucher display details.
            // Stripe shows it in a modal (handleNextAction), but our UX requires redirect.
            // Stripe does not provide an automatic redirect flow for Multibanco here,
            // so we skip action handling and continue to our own redirect below.
            if (activePaymentMethod === PaymentMethodEnum.StripeMultibanco) {
              error = undefined
            } else {
              const { error: stripeError } = await stripe.handleNextAction({
                clientSecret: mainOfferData.intentSecret,
              })
              error = stripeError
            }
          } else {
            const mainOfferStripeConfirmHandler = getStripeConfirmHandler(
              stripe,
              mainOfferData.intentType,
            )
            //https://app.shortcut.com/systeme/story/73336/revert-stripe
            // const { error: stripeError } = await mainOfferStripeConfirmHandler({
            //   elements,
            //   clientSecret: mainOfferData.intentSecret,
            //   redirect: 'if_required',
            //   confirmParams: {
            //     return_url: mainOfferData.redirect,
            //     payment_method_data: paymentMethodData,
            //   },
            // })
            // const currentPlan = pricePlans.find(
            //   plan => plan.id === activePlanId,
            // )
            const { error: stripeError } = await mainOfferStripeConfirmHandler({
              elements,
              clientSecret: mainOfferData.intentSecret,
              confirmParams: {
                return_url: mainOfferData.redirect,
                payment_method_data: paymentMethodData,
              },
            })
            error = stripeError
            //https://app.shortcut.com/systeme/story/73336/revert-stripe
            // if (!stripeError) {
            //   if (
            //     (currentPlan?.type === PricePlanTypeEnum.LimitedSubscription ||
            //       currentPlan?.type === PricePlanTypeEnum.Subscription) &&
            //     stripePaymentMethod === PaymentMethodEnum.StripeCard &&
            //     mainOfferData.purchaseProcessId
            //   ) {
            //     const { data: mainOfferSubscriptionCardData } =
            //       await buyMainOfferSubscriptionCard(
            //         pageId,
            //         mainOfferData.purchaseProcessId,
            //       )
            //     if (mainOfferSubscriptionCardData.isNextActionNeeded) {
            //       const { error: stripeErrorCard } =
            //         await stripe.handleNextAction({
            //           clientSecret: mainOfferSubscriptionCardData.intentSecret,
            //         })
            //       error = stripeErrorCard
            //     } else {
            //       window.location.href = mainOfferSubscriptionCardData.redirect
            //     }
            //   } else {
            //     window.location.href = mainOfferData.redirect
            //   }
            // }
            // error = stripeError
          }

          if (!error) {
            if (activePaymentMethod === PaymentMethodEnum.StripeMultibanco) {
              window.location.assign(mainOfferData.redirect)
              return
            }
            const { data: bumpData } = await confirmPaymentIntent(
              pageId,
              purchaseProcessId,
            )
            // bump case
            if (bumpData.intentSecret) {
              // if (
              //   bumpData.intentType !== (elements as any)?._commonOptions?.mode
              // ) {
              //   await elements.update({ mode: bumpData.intentType })
              // }
              const bumpOfferStripeConfirmHandler = getStripeConfirmHandler(
                stripe,
                bumpData.intentType,
              )

              const { error } = await bumpOfferStripeConfirmHandler({
                elements,
                clientSecret: bumpData.intentSecret,
                confirmParams: {
                  payment_method_data: paymentMethodData,
                  return_url: bumpData.redirect,
                },
              })

              if (!error) {
                const { data } = await confirmPaymentIntent(
                  pageId,
                  purchaseProcessId,
                )
                window.location.assign(data.redirect)
                return
              } else {
                // if (
                //   error.code &&
                //   !STRIPE_IGNORED_ERROR_CODES_LIST.includes(error.code)
                // ) {
                //   rollbar.error('stripe bump offer error', error)
                // }
                setErrors([error.message as string])
              }
            }

            // redirect to upSell or thank you page
            window.location.assign(mainOfferData.redirect)
          } else {
            // if (
            //   error.code &&
            //   !STRIPE_IGNORED_ERROR_CODES_LIST.includes(error.code)
            // ) {
            //   rollbar.error('stripe main offer error', error)
            // }
            setErrors([error.message as string])
          }
        } else {
          // redirect to upSell or thank you page
          window.location.href = mainOfferData.redirect
        }
      },
      (error, body) => {
        if (error instanceof BadRequest) {
          setErrors(error.response.data.errors.common)
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (
          error instanceof InternalError ||
          error instanceof ServiceUnavailable
        ) {
          setErrors([t('core.error.title')])
        } else {
          rollbar.captureEvent(body!, 'debug')
          rollbar.error(error as Error)
        }
      },
    )
  }
  return { errors, isReady, isLoading, handleSubmit }
}
